/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import {
  type Campaign,
  type CustomerCampaign,
  type DiscountDefinition,
  fetchCustomerCampaignByLookupKey,
  isCoreSeatDiscount,
} from 'embercom/lib/billing/campaign';
import moment from 'moment-timezone';

const FREE_SEATS_WINDOW_DAYS = 30;
export const FREE_SEATS_CAMPAIGN_LOOKUP_KEY = 'campaign_free_seats_offer';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';

interface FreeSeatsCampaignMetadata {
  automationRate: number;
  finIsLive: boolean;
  subscriptionPhase: 'in_trial' | 'within_free_seats_window' | 'past_free_seats_window';
  currentCampaign?: CustomerCampaign;
}

export default class CompellingOfferCampaignService extends Service {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare aiAgentSetupService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked campaign: Campaign | null = null;

  @keepLatestTask
  *fetchCampaign(): TaskGenerator<void> {
    if (this.currentCampaign?.lookup_key) {
      this.campaign = yield fetchCustomerCampaignByLookupKey(
        this.appService.app.id,
        this.currentCampaign.lookup_key,
      );
    }
  }

  get currentCampaign(): CustomerCampaign | undefined {
    return this.customerService.customer?.currentCampaign;
  }

  get isEnrolled(): boolean {
    return !!this.currentCampaign || this.appService.app.isForcedFreeSeatsCampaignEnrolment;
  }

  coreSeatDiscountForPlan(planId: string): DiscountDefinition | undefined {
    return this.campaign?.discount_definitions?.find(
      (dd) => dd.plan_id.toString() === planId && isCoreSeatDiscount(dd),
    );
  }

  get isEligibleForDiscountBanner(): boolean {
    return (
      !!this.currentCampaign &&
      !this.currentCampaign.redeemed &&
      !!this.currentCampaign.redemption_expires_at &&
      new Date(this.currentCampaign.redemption_expires_at) > new Date()
    );
  }

  get compellingOfferBannerText() {
    if (!this.currentCampaign || this.isFreeSeatsCampaign) {
      return '';
    }
    return this.intl.t(`components.banners.campaigns.${this.currentCampaign.lookup_key}-text`);
  }

  get isFreeSeatsCampaign(): boolean {
    return (
      this.currentCampaign?.lookup_key === FREE_SEATS_CAMPAIGN_LOOKUP_KEY ||
      this.appService.app.isForcedFreeSeatsCampaignEnrolment
    );
  }

  /*
    Returns metadata for the free seats campaign, including the current
    subscription phase:
    - in_trial: app is in trial or expired trial
    - within_free_seats_window: app is within FREE_SEATS_WINDOW_DAYS of a paid subscription
    - past_free_seats_window: app is past FREE_SEATS_WINDOW_DAYS of a paid subscription
  */
  get freeSeatsCampaignMetadata(): FreeSeatsCampaignMetadata {
    // TODO: Wire in real automation rate from campaign metadata
    let automationRate = 0.435;
    let finIsLive = !!this.aiAgentSetupService.isFinLive;
    let currentCampaign = this.currentCampaign;

    if (this.cardlessTrialService.inTrialOrExpiredTrial) {
      return { automationRate, finIsLive, subscriptionPhase: 'in_trial', currentCampaign };
    }

    let customer = this.customerService.customer;
    if (!customer) {
      return { automationRate, finIsLive, subscriptionPhase: 'past_free_seats_window' };
    }

    let startedAt = customer.subscription?.startedAt;
    let daysSinceSubscription = moment().diff(startedAt, 'days');

    if (customer.hasActiveSubscription && daysSinceSubscription <= FREE_SEATS_WINDOW_DAYS) {
      return {
        automationRate,
        finIsLive,
        subscriptionPhase: 'within_free_seats_window',
        currentCampaign,
      };
    }

    return { automationRate, finIsLive, subscriptionPhase: 'past_free_seats_window' };
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferCampaignService: CompellingOfferCampaignService;
    'compelling-offer-campaign-service': CompellingOfferCampaignService;
  }
}
